import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { SaleOrder } from 'models/SaleOrderModel';
import { FormList } from "models/BaseModel";
import AsyncSelect from "react-select/async";
import { useSearchParams } from 'react-router-dom';

const SaleOrderForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.SALEORDER, Code.Form.Name.SALEORDER];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [formData, setFormData] = useState<SaleOrder>({});
  const [insurerOptions, setInsurerOptions] = Hooks.Form.Options();
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [creditOptions, setCreditOptions] = Hooks.Form.Options();
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [mopOptions, setMopOptions] = Hooks.Form.Options();
  const [orderStatusOptions, setOrderStatusOptions] = Hooks.Form.Options();
  const [formListData, setFormListData] = useState<FormList>({});

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const saleId = searchParams.get(Code.UrlPara.Sale);
  const segmentId = searchParams.get(Code.UrlPara.Segment);

  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.DOCTOR, Code.Option.Common.NA, setDoctorOptions, setError);
    Files.Json.API(Code.File.INSURER, Code.Option.Common.NA, setInsurerOptions, setError);
    Files.Json.Http(Code.File.CREDIT+Code.File.TYPE, Code.Option.Common.SELECT, setCreditOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.ORDER_STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.ORDER_TYPE, Code.Option.Common.NA, setTypeOptions, setError);
    Files.Json.API(Code.File.MOP, Code.Option.Common.SELECT, setMopOptions, setError);
   
    API.Data.GetByKeyId<SaleOrder>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.saleorder_active); 
        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          ...clientData,
        }));
        setFormListData(await Form.Map.Data<FormList>(JSON.parse(data.saleorder_data!)));
        setLoading(false);
      }})

    const onNewEntry = async () => {
      if(keyId===Code.Key.NEW && clientId) {
        const clientName = await API.Data.GetClientName(Number(clientId));
        setFormData((prevData) => ({
          ...prevData,
          client_id: Number(clientId) || 21,
          client_name: Utility.Value.Text(clientName), 
          sale_id: Number(saleId) || 2,
          segment_id: Number(segmentId) || 2,
        }));
      }
      setLoading(false);
    };
    onNewEntry();

  }, []);

 

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    if (formData) {

      const addonData = await API.Data.AddonData(formData, formName, keyId);
      const finalData = Utility.Sanitize.FormData({
        ...formData,
        ...addonData,
        saleorder_data: Utility.Clean.JsonObject(formListData),
      });
       
      API.Data.Save(formName, keyId, finalData, setMessage)
    }
  };

  useEffect(() => {
    console.log("Form List Data changed----->", formListData);
  }, [formListData]);

  useEffect(() => {
  let creditId = Number(formData.credit_id) || 0;
  let payableAmount = Number(formData.saleorder_payable_amount) || 0;
  let clientAmount = Number(formData.saleorder_client_amount) || 0;
  let insurer1Amount = Number(formData.saleorder_insurer1_amount) || 0;
  let insurer2Amount = Number(formData.saleorder_insurer2_amount) || 0;
  let giftAmount = Number(formData.gift_amount) || 0;
  let frameAmount = Number(formData.frame_amount) || 0;
  let highIndexAmount = Number(formData.highindex_amount) || 0;
  let creditUse = Number(formData.credit_use_amount) || 0;
  let creditAdd = Number(formData.credit_add_amount) || 0;

  let newClientAmount = clientAmount;
  let newCreditAdd = creditAdd;

  if (creditId === 22) { // Instore Credit
    newClientAmount = payableAmount - (insurer1Amount + insurer2Amount + giftAmount);
    newCreditAdd = (insurer1Amount + insurer2Amount + newClientAmount) - (frameAmount + highIndexAmount);
  } else {
    newClientAmount = (payableAmount + frameAmount + highIndexAmount) -
                      (insurer1Amount + insurer2Amount + giftAmount + creditUse);
    newCreditAdd = 0;
  }

  // ✅ Update only if changed (prevents infinite loops)
  if (
    newClientAmount !== formData.saleorder_client_amount ||
    newCreditAdd !== formData.credit_add_amount
  ) {
    setFormData(prev => ({
      ...prev,
      saleorder_client_amount: Number(newClientAmount.toFixed(2)),
      credit_add_amount: Number(newCreditAdd.toFixed(2)),
    }));
  }

  let order1 = formListData.order1;
  if(payableAmount>0){
    if(order1 == null) order1 = {id: 2, status: 2};
  } else order1 = null;

  let proforma1 = formListData.proforma1;
  if(creditUse>0){
    if(proforma1 == null) proforma1 = {id: 2, status: 2};
  } else proforma1 = null;

  let invoice1 = formListData.invoice1;
  let payment1 = formListData.payment1;
  let payment2 = formListData.payment2;
  let payment3 = formListData.payment3;
  if (newClientAmount > 0 || insurer1Amount > 0 || insurer2Amount > 0){
    if(invoice1 == null) invoice1 = {id: 2, status: 2};
 
    if(newClientAmount>0) {
      if(payment1 == null) payment1 = {id: 2, status: 2};
    } else payment1 = null;
 
    if(insurer1Amount>0) {
      if(payment2 == null) payment2 = {id: 2, status: 2};
    } else payment2 = null;

    if(insurer2Amount>0) {
      if(payment3 == null) payment3 = {id: 2, status: 2};
    } else payment3 = null;

  }

  setFormListData((prevData) => ({
    ...prevData,
    order1: order1,
    invoice1: invoice1,
    payment1: payment1,
    payment2: payment2,
    payment3: payment3,
  }));
}, [
  formData.credit_id,
  formData.saleorder_payable_amount,
  formData.saleorder_insurer1_amount,
  formData.saleorder_insurer2_amount,
  formData.gift_amount,
  formData.frame_amount,
  formData.highindex_amount,
  formData.credit_use_amount,
]);

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Item Details"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Select id={Name.Common.CreditId} value={formData.credit_id} title={Title.Category} options={creditOptions} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} require={true} />
                <Field.Select id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.State} options={statusOptions} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Row>
                  <Field.Amount id={Name.SaleOrder.Payable} value={formData.saleorder_payable_amount} title='Total Payable' onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} />
                  <Field.Amount id={Name.SaleOrder.ClientAmount} value={formData.saleorder_client_amount} title='Client Pay' onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
                  <Field.Amount id={Name.SaleOrder.HighIndexAmount} value={formData.highindex_amount} title='High-Index' onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]} />
              </Control.Row>
              <Control.Row>
                  <Field.Amount id={Name.SaleOrder.FrameAmount} value={formData.frame_amount} title='Case' onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} />
                  <Field.Amount id={Name.SaleOrder.CreditUse} value={formData.credit_use_amount} title='Credit Use' onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
                  <Field.Amount id={Name.SaleOrder.CreditAdd} value={formData.credit_add_amount} title='Credit Add' onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Title title="&nbsp;&nbsp;Gift Card" size={[0,33,1]} />
                <Field.Amount id={Name.SaleOrder.GiftAmount} value={formData.gift_amount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
                <Field.FilterSelect id={Name.Common.ModeId} value={formData.mode_id} options={mopOptions} setFormData={setFormData} size={[1,33,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Title title="&nbsp;&nbsp;Insurance 1" size={[0,33,1]} />
                <Field.Amount id={Name.SaleOrder.Insurer1Amount} value={formData.saleorder_insurer1_amount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
                <Field.FilterSelect id={Name.Common.insurer1Id} value={formData.insurer1_id} options={insurerOptions} setFormData={setFormData} size={[1,33,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Title title="&nbsp;&nbsp;Insurance 2" size={[0,33,1]} />
                <Field.Amount id={Name.SaleOrder.Insurer2Amount} value={formData.saleorder_insurer2_amount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
                <Field.FilterSelect id={Name.Common.insurer2Id} value={formData.insurer2_id} options={insurerOptions} setFormData={setFormData} size={[1,33,0]} />
              </Control.Row>
              <Control.Row>
                <Field.FilterSelect id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions} setFormData={setFormData} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.SaleOrder.Comment} value={formData.saleorder_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
            </Control.Tab.Content>

          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}>
        <Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} />
      </div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default SaleOrderForm;